// Chart X-axis helpers — distance (km) or elapsed time, never raw GPS/coords.

import { fmtDuration, distanceUnit, distanceValue } from "./format";

/** True when distance_m looks like cumulative meters, not lat/lon. */
export function hasValidDistance(samples) {
  if (!samples?.length) return false;

  const dists = samples
    .map((s) => s.distance_m)
    .filter((d) => typeof d === "number" && !Number.isNaN(d) && d >= 0);

  if (dists.length < 2) return false;

  const max = Math.max(...dists);
  const last = dists[dists.length - 1];
  if (max < 100) return false;

  let monotonic = 0;
  for (let i = 1; i < dists.length; i++) {
    if (dists[i] >= dists[i - 1] - 1) monotonic++;
  }
  const monoRatio = monotonic / (dists.length - 1);
  if (monoRatio < 0.8) return false;

  // Lat/lon masquerading as distance: small range, never reaches ride scale.
  if (max <= 180 && last < 1000 && dists[0] > 0.5 && dists[0] < 180) {
    return false;
  }

  return last >= max * 0.85 || max >= 1000;
}

/** Clean km tick — e.g. 0, 5, 10 or 0.0, 0.5, 1.0 for short laps. */
export function formatDistanceTick(value, maxValue, unitSystem = "metric") {
  if (value == null || Number.isNaN(value)) return "";
  const v = Number(value);
  const max = maxValue ?? v;

  if (max < 1) return v.toFixed(1);
  if (max < 10) return v.toFixed(1);
  return String(Math.round(v));
}

/** Elapsed seconds → 0:00, 15:00, 30:00 */
export function formatTimeTick(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "";
  return fmtDuration(Math.max(0, Math.round(seconds)));
}

export function buildActivityChartSeries(samples, activityStartTime, unitSystem = "metric") {
  if (!samples?.length) {
    return {
      data: [],
      xKey: "distance",
      xUnit: distanceUnit(unitSystem),
      xTickFormatter: (v) => formatDistanceTick(v, 0, unitSystem),
    };
  }

  if (hasValidDistance(samples)) {
    const maxDist = Math.max(...samples.map((s) => distanceValue(s.distance_m || 0, unitSystem)));
    const data = samples.map((s) => ({
      distance: distanceValue(s.distance_m || 0, unitSystem),
      hr: s.hr,
      power: s.power,
      cadence: s.cadence,
      speed_kmh: s.speed_ms != null ? s.speed_ms * 3.6 : null,
      elevation: s.altitude_m,
    }));

    return {
      data,
      xKey: "distance",
      xUnit: distanceUnit(unitSystem),
      xTickFormatter: (v) => formatDistanceTick(v, maxDist, unitSystem),
    };
  }

  const startMs = activityStartTime
    ? new Date(activityStartTime).getTime()
    : samples[0]?.t
      ? new Date(samples[0].t).getTime()
      : 0;

  const data = samples.map((s, i) => {
    let elapsed_s = i;
    if (s.t && startMs) {
      const t = new Date(s.t).getTime();
      if (!Number.isNaN(t)) elapsed_s = Math.max(0, (t - startMs) / 1000);
    }
    return {
      elapsed_s,
      hr: s.hr,
      power: s.power,
      cadence: s.cadence,
      speed_kmh: s.speed_ms != null ? s.speed_ms * 3.6 : null,
      elevation: s.altitude_m,
    };
  });

  const maxSeconds = Math.max(...data.map((d) => d.elapsed_s));

  return {
    data,
    xKey: "elapsed_s",
    xUnit: "time",
    xTickFormatter: (v) => formatTimeTick(v),
    maxX: maxSeconds,
  };
}
